import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled, { keyframes } from 'styled-components';

import { Billboard, Button } from '@companycam/slab-web';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';
import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';

import { NoContent } from './EmptyFeed';
import { LD_NEW_EMPTY_STATES } from 'constants/featureFlags';
import PhotosQRCode from 'images/mobile_one_link_qr_code.svg';
import { useCurrentUser } from 'javascripts/hooks';
import { translate } from 'javascripts/translations/i18n';

type CompanyEmptyAssetFeedType = {
  parentType: string;
};

const billboardEnter = keyframes`
to {
  opacity: 1;
}
`;

const BorderedBillboard = styled(Billboard)`
  opacity: 0;
  animation-name: ${billboardEnter};
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  border: var(--cc_size_border_width_m) solid
    var(--cc_color_button_background_subtle);
  border-radius: var(--cc_size_border_radius_l);
  margin-top: var(--cc_size_spacing_xl);
  padding: 3%;
  p {
    margin-top: var(--cc_size_spacing_s);
  }
  button {
    margin-top: var(--cc_size_spacing_m);
  }
`;

const StyledBillboard = styled(Billboard)`
  opacity: 0;
  animation-name: ${billboardEnter};
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  p {
    margin-top: var(--cc_size_spacing_s);
  }
  button {
    margin-top: var(--cc_size_spacing_m);
  }
`;

const QRCode = styled.img`
  margin-top: var(--cc_size_spacing_m);
`;

const QRBillboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: var(--cc_size_border_width_m) solid
    var(--cc_color_button_background_subtle);
  border-radius: var(--cc_size_border_radius_l);
  padding: 3%;
  align-items: center;
`;

function CompanyEmptyAssetFeed({ parentType }: CompanyEmptyAssetFeedType) {
  const flags = useFlags();
  const shouldShowNewEmptyState = flags[LD_NEW_EMPTY_STATES];
  const user = useCurrentUser();
  const canInviteUsers = hasRoleAccess(user, [ADMIN, MANAGER]);

  const onInviteUsersClick = () => {
    Steven.elementClicked({
      id: 'photos_empty_state_invite_users_button',
      content: 'assetFeed.emptyState.emptyStateInviteButton',
      type: ELEMENT_TYPES.BUTTON,
      action: 'photos_empty_state_invite_users_button_clicked',
    });

    window.Turbo.visit('/invitations/new');
  };

  return shouldShowNewEmptyState ? (
    <>
      <QRBillboardContainer>
        <StyledBillboard
          title={translate('assetFeed.emptyState.emptyStateQRTitle')}
          message={translate('assetFeed.emptyState.emptyStateQRMessage')}
        />
        <QRCode src={PhotosQRCode} alt="QR Code" />
      </QRBillboardContainer>
      {canInviteUsers ? (
        <BorderedBillboard
          title={translate('assetFeed.emptyState.emptyStateInviteTitle')}
          message={translate('assetFeed.emptyState.emptyStateInviteMessage')}
          secondaryAction={{
            icon: {
              name: 'plus-circle-outline',
              position: 'before',
            },
            label: translate('assetFeed.emptyState.emptyStateInviteButton'),
            onClick: onInviteUsersClick,
            'data-testid': 'photo-feed__invite-users',
          }}
        />
      ) : null}
    </>
  ) : (
    <NoContent type={parentType}>
      <Button color="primary" size="large" href="/projects/new">
        {translate('assetFeed.emptyState.createProject')}
      </Button>
    </NoContent>
  );
}

export default CompanyEmptyAssetFeed;
